import type { ImageContent, PlaceholderValues } from "@homeinstead/schemas";
import type { PortableTextBlock } from "@portabletext/types";
import type { InfoPanel, InfoPanelItem } from "./form-types";

import Quote from "@homeinstead/icons/Quote";
import Quotes from "@homeinstead/icons/Quotes";
import Service from "@homeinstead/icons/Service";
import { cn } from "@homeinstead/ui";
import { RichText } from "@homeinstead/ui/components/RichText";
import Image from "next/image";
import { HeroImage } from "~www/components/LeadForm/CareSeeker/shared/HeroImage";
import { urlFor } from "~www/lib/sanity/sanity-images";

const VISIBILITY_CLASS: Record<string, string> = {
  always: "flex",
  "xl+": "hidden xl:flex",
};

// Single source of truth for icon sizes. cls drives Tailwind; px drives next/image
// so it generates the correct srcset instead of serving an oversized image.
const ICON_SIZES = {
  lg: { cls: "h-16 w-16", px: 64 },
  md: { cls: "h-12 w-12", px: 48 },
  sm: { cls: "h-8 w-8", px: 32 },
} satisfies Record<"sm" | "md" | "lg", { cls: string; px: number }>;

const BREAKPOINT_CLASS: Record<string, string> = {
  "2xl": "hidden 2xl:flex",
  lg: "hidden lg:flex",
  md: "hidden md:flex",
  sm: "hidden sm:flex",
  xl: "hidden xl:flex",
};

// Shown from md inside a button-stack form. `hidden` still wins everywhere else.
const BUTTON_STACK_PANEL_FLEX =
  "group-data-[layout=buttonStack]/lead-form:md:flex";
const BUTTON_STACK_PANEL_PADDING =
  "group-data-[layout=buttonStack]/lead-form:md:pr-6";
const BUTTON_STACK_HERO_IMAGE =
  "group-data-[layout=buttonStack]/lead-form:md:flex group-data-[layout=buttonStack]/lead-form:md:flex-col group-data-[layout=buttonStack]/lead-form:md:items-start";

// Padding starts at the same breakpoint that reveals the panel. `md:pr-6`
// already applies at every wider width, so there is no separate cutoff prop.
const PADDING_CLASS: Record<string, string> = {
  "2xl": "2xl:pr-6",
  lg: "lg:pr-6",
  md: "md:pr-6",
  sm: "sm:pr-6",
  xl: "xl:pr-6",
};

const ALIGNMENT_CLASS: Record<string, string> = {
  center: "my-auto",
  end: "mt-auto",
  start: "mb-auto",
};

const TEXT_ALIGNMENT_CLASS: Record<string, string> = {
  center: "items-center text-center",
  left: "items-start text-left",
  right: "items-end text-right",
};

const MAX_WIDTH_CLASS: Record<string, string> = {
  lg: "max-w-lg",
  md: "max-w-md",
  sm: "max-w-sm",
};

function resolveVisibilityClass(panel: InfoPanel): string {
  const visibility = panel.visibility ?? "xl+";
  if (visibility === "always") {
    return "flex";
  }
  if (visibility === "from-breakpoint") {
    return cn(
      BREAKPOINT_CLASS[panel.showFromBreakpoint ?? "xl"] ?? "hidden xl:flex",
      BUTTON_STACK_PANEL_FLEX,
    );
  }
  return cn(
    VISIBILITY_CLASS[visibility] ?? "hidden xl:flex",
    BUTTON_STACK_PANEL_FLEX,
  );
}

function resolvePaddingClass(panel: InfoPanel): string {
  const padding =
    (panel.visibility ?? "xl+") === "from-breakpoint"
      ? (PADDING_CLASS[panel.showFromBreakpoint ?? "xl"] ?? "xl:pr-6")
      : "xl:pr-6";
  return cn(padding, BUTTON_STACK_PANEL_PADDING);
}

function InfoPanelIconItem({
  item,
}: {
  item: Extract<InfoPanelItem, { _type: "infoPanelIcon" }>;
}) {
  // "?? md" guards against Sanity documents that pre-date the size field.
  const { cls, px } = ICON_SIZES[item.size ?? "md"];

  switch (item.icon) {
    case "default-quote-circle":
      return (
        <div
          className={cn(
            cls,
            "flex shrink-0 items-center justify-center rounded-full bg-white",
          )}
          data-testid="icon-container"
        >
          <Quotes className="h-1/3 w-1/3 fill-new-beginnings-base" />
        </div>
      );
    // quote and service share identical wrapper structure — only the SVG differs.
    case "quote":
    case "service": {
      const Icon = item.icon === "quote" ? Quote : Service;
      return (
        <div
          className={cn(cls, "flex shrink-0 items-center justify-center")}
          data-testid="icon-container"
        >
          <Icon className="h-full w-full" />
        </div>
      );
    }
    case "custom": {
      if (!item.customIcon) {
        return null;
      }
      return (
        <Image
          alt="Info panel icon"
          className={cn(cls, "shrink-0")}
          data-testid="icon-container"
          height={px}
          src={urlFor(item.customIcon).url()}
          width={px}
        />
      );
    }
    default:
      return null;
  }
}

const RICH_TEXT_ALIGNMENT_CLASS: Record<string, string> = {
  center: "text-center",
  left: "text-left",
  none: "",
  right: "text-right",
};

function InfoPanelItemRenderer({
  item,
  placeholderValues,
  textAlignment,
}: {
  item: InfoPanelItem;
  placeholderValues?: PlaceholderValues;
  textAlignment?: string;
}) {
  if (item._type === "infoPanelIcon") {
    return <InfoPanelIconItem item={item} />;
  }

  if (item._type === "richTextBlock") {
    const value = Array.isArray(item.text)
      ? (item.text as PortableTextBlock[])
      : [];
    if (value.length === 0) {
      return null;
    }
    const alignment =
      RICH_TEXT_ALIGNMENT_CLASS[
        textAlignment ?? item.textAlignment ?? "left"
      ] ?? "text-left";
    return (
      <RichText
        className={cn("max-w-none", alignment)}
        placeholders={placeholderValues}
        value={value}
      />
    );
  }

  return null;
}

function InfoPanelContent({
  infoPanel,
  placeholderValues,
  heroImageLarge,
  heroImageSmall,
}: {
  infoPanel: InfoPanel;
  placeholderValues?: PlaceholderValues;
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
}) {
  if (infoPanel.mode === "hero") {
    return (
      <HeroImage
        breakpoint={infoPanel.showFromBreakpoint ?? "xl"}
        heroImageLarge={heroImageLarge ?? infoPanel.heroImageLarge ?? undefined}
        heroImageSmall={heroImageSmall ?? infoPanel.heroImageSmall ?? undefined}
        largeViewportClassName={BUTTON_STACK_HERO_IMAGE}
        showCareGiverCTA={infoPanel.showCaregiverCTA ?? true}
        showLargeViewportImage
        showSmallViewportImage={false}
      />
    );
  }

  return (
    <>
      {infoPanel.items?.map((item) => (
        <InfoPanelItemRenderer
          item={item}
          key={item._key}
          placeholderValues={placeholderValues}
          textAlignment={infoPanel.textAlignment}
        />
      ))}
    </>
  );
}

function LeadFormInfoPanel({
  infoPanel,
  placeholderValues,
  heroImageLarge,
  heroImageSmall,
}: {
  infoPanel: InfoPanel;
  placeholderValues?: PlaceholderValues;
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
}) {
  const isHeroMode = infoPanel.mode === "hero";
  const alignmentClass = ALIGNMENT_CLASS[infoPanel.alignment ?? "start"] ?? "";

  if (isHeroMode) {
    // Hero image panels need no stack padding — the form content's pb-14 already
    // provides visual separation on mobile. Adding pt-14 here creates an unwanted
    // gap above the image on small viewports (matching NationalHero's behaviour).
    return (
      <div
        className={cn("flex-col", alignmentClass)}
        data-testid="lead-form-info-panel"
        data-variant={infoPanel.variant ?? "default"}
      >
        <InfoPanelContent
          heroImageLarge={heroImageLarge}
          heroImageSmall={heroImageSmall}
          infoPanel={infoPanel}
          placeholderValues={placeholderValues}
        />
      </div>
    );
  }

  const visibilityClass = resolveVisibilityClass(infoPanel);
  const textAlignmentClass =
    TEXT_ALIGNMENT_CLASS[infoPanel.textAlignment ?? "right"] ?? "";
  const maxWidthClass = MAX_WIDTH_CLASS[infoPanel.maxWidth ?? "md"] ?? "";

  return (
    <div
      className={cn(
        "ml-auto flex-col gap-4",
        resolvePaddingClass(infoPanel),
        visibilityClass,
        alignmentClass,
        textAlignmentClass,
        maxWidthClass,
      )}
      data-testid="lead-form-info-panel"
    >
      <InfoPanelContent
        infoPanel={infoPanel}
        placeholderValues={placeholderValues}
      />
    </div>
  );
}

export type { InfoPanel } from "./form-types";

export { LeadFormInfoPanel };
